import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Users, UserCheck, UserX, Trash2, Search, ArrowLeft, Crown, Calendar, ToggleLeft, ToggleRight, Pencil, X, Save, BarChart3, CalendarDays } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ANIMAL_TO_CHART_ID = {
    'Rat': 'x7k2m', 'Ox': 'p3n9q', 'Tiger': 'v8j4w', 'Rabbit': 'r2f6y',
    'Dragon': 'h5c1z', 'Snake': 'b9t3x', 'Horse': 'n4g8k', 'Goat': 'm1d7p',
    'Monkey': 'q6s2v', 'Rooster': 'w3h9n', 'Dog': 'y8b4j', 'Pig': 'c5r1f'
};

// Edit User Modal Component
function EditUserModal({ user, onClose, onSave, token }) {
    const [formData, setFormData] = useState({
        name: user.name || '',
        email: user.email || '',
        password: '',
        birth_year: user.birth_year || '',
        birth_month: user.birth_month || '',
        birth_day: user.birth_day || '',
        birth_hour: user.birth_hour || '',
        birth_minute: user.birth_minute || '',
        membership_start_date: user.membership_start_date || '',
        membership_end_date: user.membership_end_date || '',
        membership_status: user.membership_status || 'active',
        has_pro_access: user.has_pro_access || false
    });
    const [saving, setSaving] = useState(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        
        try {
            const updateData = {
                name: formData.name,
                email: formData.email,
                birth_year: formData.birth_year ? parseInt(formData.birth_year) : null,
                birth_month: formData.birth_month ? parseInt(formData.birth_month) : null,
                birth_day: formData.birth_day ? parseInt(formData.birth_day) : null,
                birth_hour: formData.birth_hour !== '' ? parseInt(formData.birth_hour) : null,
                birth_minute: formData.birth_minute !== '' ? parseInt(formData.birth_minute) : null,
                membership_start_date: formData.membership_start_date || null,
                membership_end_date: formData.membership_end_date || null,
                membership_status: formData.membership_status || null,
                has_pro_access: formData.has_pro_access
            };
            
            // Only include password if it's been changed
            if (formData.password) {
                updateData.password = formData.password;
            }

            const res = await fetch(`${API_URL}/api/auth/admin/users/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(updateData)
            });

            if (res.ok) {
                toast.success('User updated successfully');
                onSave();
                onClose();
            } else {
                const error = await res.json();
                toast.error(error.detail || 'Failed to update user');
            }
        } catch (err) {
            console.error('Failed to update user:', err);
            toast.error('Failed to update user');
        }
        
        setSaving(false);
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-xl font-bold text-slate-800">Edit User</h2>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
                        <X className="w-5 h-5 text-slate-500" />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="Enter name"
                        />
                    </div>
                    
                    {/* Email */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="Enter email"
                            required
                        />
                    </div>
                    
                    {/* Password */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            New Password <span className="text-slate-400 font-normal">(leave blank to keep current)</span>
                        </label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            placeholder="Enter new password"
                        />
                    </div>
                    
                    {/* Date of Birth */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Date of Birth</label>
                        <div className="grid grid-cols-3 gap-2">
                            <input
                                type="number"
                                name="birth_year"
                                value={formData.birth_year}
                                onChange={handleChange}
                                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                placeholder="Year"
                                min="1920"
                                max="2025"
                            />
                            <select
                                name="birth_month"
                                value={formData.birth_month}
                                onChange={handleChange}
                                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            >
                                <option value="">Month</option>
                                {[...Array(12)].map((_, i) => (
                                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                                ))}
                            </select>
                            <select
                                name="birth_day"
                                value={formData.birth_day}
                                onChange={handleChange}
                                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            >
                                <option value="">Day</option>
                                {[...Array(31)].map((_, i) => (
                                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    {/* Time of Birth */}
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Time of Birth</label>
                        <div className="grid grid-cols-2 gap-2">
                            <select
                                name="birth_hour"
                                value={formData.birth_hour}
                                onChange={handleChange}
                                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            >
                                <option value="">Hour</option>
                                {[...Array(24)].map((_, i) => (
                                    <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                                ))}
                            </select>
                            <select
                                name="birth_minute"
                                value={formData.birth_minute}
                                onChange={handleChange}
                                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            >
                                <option value="">Minute</option>
                                {[...Array(60)].map((_, i) => (
                                    <option key={i} value={i}>:{i.toString().padStart(2, '0')}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    
                    {/* Membership Section */}
                    <div className="border-t pt-4 mt-4">
                        <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <Crown className="w-4 h-4 text-amber-500" />
                            Membership Settings
                        </h3>
                        
                        {/* PRO Access Toggle */}
                        <div className="flex items-center justify-between mb-4 p-3 bg-amber-50 rounded-lg">
                            <span className="text-sm font-medium text-slate-700">PRO Access</span>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="has_pro_access"
                                    checked={formData.has_pro_access}
                                    onChange={handleChange}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                            </label>
                        </div>
                        
                        {/* Membership Status */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-slate-700 mb-1">Membership Status</label>
                            <select
                                name="membership_status"
                                value={formData.membership_status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            >
                                <option value="">Select status</option>
                                <option value="active">Active</option>
                                <option value="trial">Trial</option>
                                <option value="expired">Expired</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="failed_payment">Failed Payment</option>
                            </select>
                        </div>
                        
                        {/* Membership Dates */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    name="membership_start_date"
                                    value={formData.membership_start_date ? formData.membership_start_date.split('T')[0] : ''}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">End Date</label>
                                <input
                                    type="date"
                                    name="membership_end_date"
                                    value={formData.membership_end_date ? formData.membership_end_date.split('T')[0] : ''}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Buttons */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {saving ? (
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Save className="w-4 h-4" />
                            )}
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function UserRow({ user, currentUserId, onPromote, onDemote, onDelete, onTogglePro, onEdit }) {
    const isCurrentUser = user.id === currentUserId;
    const [loading, setLoading] = useState(false);
    
    const handleAction = async (action) => {
        setLoading(true);
        await action();
        setLoading(false);
    };
    
    return (
        <tr className="border-b border-slate-100 hover:bg-slate-50">
            <td className="py-4 px-4">
                <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${user.is_admin ? 'bg-purple-500' : user.has_pro_access ? 'bg-amber-500' : 'bg-slate-400'}`}>
                        {user.name?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <div className="font-medium text-slate-800 flex items-center gap-2">
                            {user.name || 'No name'}
                            {user.is_admin && <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">Admin</span>}
                            {isCurrentUser && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded">You</span>}
                        </div>
                        <div className="text-sm text-slate-500">{user.email}</div>
                    </div>
                </div>
            </td>
            <td className="py-4 px-4">
                {user.birth_animal ? (
                    <div className="flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-sm">
                            {user.birth_animal_chinese} {user.birth_animal}
                        </span>
                        <button
                            onClick={() => {
                                const chartId = ANIMAL_TO_CHART_ID[user.birth_animal];
                                if (chartId) {
                                    const name = encodeURIComponent(user.name || user.email);
                                    window.open(`/energy-chart/${chartId}?guest_name=${name}`, '_blank');
                                }
                            }}
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="View Energy Chart"
                            data-testid={`user-energy-${user.id}`}
                        >
                            <BarChart3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={() => {
                                const name = encodeURIComponent(user.name || user.email);
                                const chinese = encodeURIComponent(user.birth_animal_chinese || '');
                                window.open(`/calendar?guest_animal=${user.birth_animal}&guest_animal_index=${user.birth_animal_index}&guest_animal_chinese=${chinese}&guest_name=${name}`, '_blank');
                            }}
                            className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="View Calendar"
                            data-testid={`user-calendar-${user.id}`}
                        >
                            <CalendarDays className="w-3.5 h-3.5" />
                        </button>
                    </div>
                ) : (
                    <span className="text-slate-400 text-sm">Not set</span>
                )}
            </td>
            <td className="py-4 px-4">
                <div className="flex items-center gap-2">
                    {user.has_pro_access ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm">
                            <Crown className="w-3 h-3" />
                            PRO
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded text-sm">
                            Beginner
                        </span>
                    )}
                    {user.membership_status && (
                        <span className={`px-2 py-0.5 rounded text-xs ${
                            user.membership_status === 'active' ? 'bg-green-100 text-green-700' :
                            user.membership_status === 'trial' ? 'bg-blue-100 text-blue-700' :
                            user.membership_status === 'expired' ? 'bg-gray-100 text-gray-600' :
                            user.membership_status === 'cancelled' ? 'bg-orange-100 text-orange-700' :
                            user.membership_status === 'failed_payment' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-600'
                        }`}>
                            {user.membership_status}
                        </span>
                    )}
                </div>
            </td>
            <td className="py-4 px-4 text-sm">
                {user.membership_start_date || user.membership_end_date ? (
                    <div className="space-y-1">
                        {user.membership_start_date && (
                            <div className="text-slate-500">
                                <span className="text-xs text-slate-400">Start:</span> {new Date(user.membership_start_date).toLocaleDateString()}
                            </div>
                        )}
                        {user.membership_end_date && (
                            <div className={`${new Date(user.membership_end_date) < new Date() ? 'text-red-500' : 'text-slate-500'}`}>
                                <span className="text-xs text-slate-400">End:</span> {new Date(user.membership_end_date).toLocaleDateString()}
                            </div>
                        )}
                    </div>
                ) : (
                    <span className="text-slate-400 text-xs">Not set</span>
                )}
            </td>
            <td className="py-4 px-4 text-sm text-slate-500">
                {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
            </td>
            <td className="py-4 px-4">
                <div className="flex items-center gap-1">
                    {/* Edit Button */}
                    <button
                        onClick={() => onEdit(user)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit user"
                    >
                        <Pencil className="w-4 h-4" />
                    </button>
                    
                    {!isCurrentUser && (
                        <>
                            {/* PRO Toggle */}
                            <button
                                onClick={() => handleAction(() => onTogglePro(user.email, !user.has_pro_access))}
                                disabled={loading}
                                className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${user.has_pro_access ? 'text-amber-600 hover:bg-amber-50' : 'text-slate-400 hover:bg-slate-100'}`}
                                title={user.has_pro_access ? 'Revoke PRO access' : 'Grant PRO access'}
                            >
                                {user.has_pro_access ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                            </button>
                            {/* Admin Toggle */}
                            {user.is_admin ? (
                                <button
                                    onClick={() => handleAction(() => onDemote(user.email))}
                                    disabled={loading}
                                    className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Remove admin"
                                >
                                    <UserX className="w-4 h-4" />
                                </button>
                            ) : (
                                <button
                                    onClick={() => handleAction(() => onPromote(user.email))}
                                    disabled={loading}
                                    className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                                    title="Make admin"
                                >
                                    <UserCheck className="w-4 h-4" />
                                </button>
                            )}
                            {/* Delete */}
                            <button
                                onClick={() => handleAction(() => onDelete(user.id, user.email))}
                                disabled={loading}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Delete user"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
}

function StatsCard({ icon: Icon, label, value, color }) {
    return (
        <div className="bg-white rounded-xl shadow-lg p-5">
            <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
                <div>
                    <div className="text-2xl font-bold text-slate-800">{value}</div>
                    <div className="text-sm text-slate-500">{label}</div>
                </div>
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const navigate = useNavigate();
    const { isAuthenticated, user, token } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [stats, setStats] = useState({ total: 0, admins: 0, proUsers: 0 });
    const [editingUser, setEditingUser] = useState(null);

    // Check admin and fetch users
    const fetchData = useCallback(async () => {
        if (!token) return;
        
        try {
            // Check admin status
            const checkRes = await fetch(`${API_URL}/api/auth/admin/check`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (checkRes.ok) {
                const checkData = await checkRes.json();
                setIsAdmin(checkData.is_admin);
                
                if (checkData.is_admin) {
                    // Fetch users
                    const usersRes = await fetch(`${API_URL}/api/auth/admin/users`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (usersRes.ok) {
                        const usersData = await usersRes.json();
                        setUsers(usersData.users);
                        const proCount = usersData.users.filter(u => u.has_pro_access).length;
                        setStats({ 
                            total: usersData.total, 
                            admins: usersData.admins,
                            proUsers: proCount
                        });
                    }
                }
            }
        } catch (err) {
            console.error('Failed to fetch data:', err);
        }
        setLoading(false);
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handlePromote = async (email) => {
        try {
            const res = await fetch(`${API_URL}/api/auth/admin/promote?email=${encodeURIComponent(email)}&admin_secret=`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchData();
            }
        } catch (err) {
            console.error('Failed to promote:', err);
        }
    };

    const handleDemote = async (email) => {
        try {
            const res = await fetch(`${API_URL}/api/auth/admin/demote?email=${encodeURIComponent(email)}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchData();
            }
        } catch (err) {
            console.error('Failed to demote:', err);
        }
    };

    const handleDelete = async (userId, email) => {
        if (!window.confirm(`Are you sure you want to delete ${email}? This cannot be undone.`)) {
            return;
        }
        
        try {
            const res = await fetch(`${API_URL}/api/auth/admin/users/${userId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchData();
            }
        } catch (err) {
            console.error('Failed to delete:', err);
        }
    };

    const handleTogglePro = async (email, grantPro) => {
        try {
            const endpoint = grantPro ? 'grant-pro' : 'revoke-pro';
            const res = await fetch(`${API_URL}/api/auth/admin/${endpoint}?email=${encodeURIComponent(email)}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchData();
            }
        } catch (err) {
            console.error('Failed to toggle PRO:', err);
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
    };

    // Filter users by search and type
    const filteredUsers = users.filter(u => {
        const matchesSearch = u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (u.name && u.name.toLowerCase().includes(searchTerm.toLowerCase()));
        if (!matchesSearch) return false;
        if (filterType === 'pro') return u.has_pro_access;
        if (filterType === 'free') return !u.has_pro_access;
        if (filterType === 'admin') return u.is_admin;
        return true;
    });

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
                    <Shield className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-slate-800 mb-2">Login Required</h1>
                    <p className="text-slate-500 mb-6">Please log in to access the admin dashboard</p>
                    <button onClick={() => navigate('/login')} className="bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 px-6 rounded-lg">
                        Log In
                    </button>
                </div>
            </div>
        );
    }

    if (!isAdmin) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md text-center">
                    <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h1>
                    <p className="text-slate-500 mb-6">You don't have admin privileges</p>
                    <button onClick={() => navigate('/')} className="bg-slate-600 hover:bg-slate-700 text-white font-semibold py-3 px-6 rounded-lg">
                        Go Home
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="admin-dashboard">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/')} className="p-2 hover:bg-white rounded-lg transition-colors">
                            <ArrowLeft className="w-5 h-5 text-slate-600" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
                                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs font-medium">
                                    <Shield className="w-3 h-3 inline mr-1" />
                                    Admin
                                </span>
                            </div>
                            <p className="text-slate-500">Manage users and access levels</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/admin/guest-profiles')}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-medium transition-colors text-sm"
                        data-testid="guest-profiles-btn"
                    >
                        <UserCheck className="w-4 h-4" />
                        Guest Profiles
                    </button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <StatsCard icon={Users} label="Total Users" value={stats.total} color="bg-blue-500" />
                    <StatsCard icon={Crown} label="PRO Users" value={stats.proUsers} color="bg-amber-500" />
                    <StatsCard icon={Shield} label="Admin Users" value={stats.admins} color="bg-purple-500" />
                    <StatsCard icon={Calendar} label="Free Users" value={stats.total - stats.proUsers} color="bg-slate-500" />
                </div>

                {/* User List */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                    <div className="p-6 border-b border-slate-100">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-2 flex-wrap" data-testid="user-filter-buttons">
                                {[
                                    { key: 'all', label: 'All', count: stats.total },
                                    { key: 'pro', label: 'PRO', count: stats.proUsers },
                                    { key: 'free', label: 'Free', count: stats.total - stats.proUsers },
                                    { key: 'admin', label: 'Admin', count: stats.admins },
                                ].map(f => (
                                    <button
                                        key={f.key}
                                        onClick={() => setFilterType(f.key)}
                                        data-testid={`filter-${f.key}`}
                                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                            filterType === f.key
                                                ? f.key === 'pro' ? 'bg-amber-500 text-white'
                                                : f.key === 'admin' ? 'bg-purple-500 text-white'
                                                : f.key === 'free' ? 'bg-slate-600 text-white'
                                                : 'bg-blue-500 text-white'
                                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                        }`}
                                    >
                                        {f.label} ({f.count})
                                    </button>
                                ))}
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border rounded-lg w-64 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                                    data-testid="user-search-input"
                                />
                            </div>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">User</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Zodiac</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Access Level</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Membership</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Joined</th>
                                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map(u => (
                                    <UserRow
                                        key={u.id}
                                        user={u}
                                        currentUserId={user?.id}
                                        onPromote={handlePromote}
                                        onDemote={handleDemote}
                                        onDelete={handleDelete}
                                        onTogglePro={handleTogglePro}
                                        onEdit={handleEdit}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    {filteredUsers.length === 0 && (
                        <div className="p-12 text-center text-slate-500">
                            {searchTerm || filterType !== 'all' 
                                ? `No ${filterType !== 'all' ? filterType.toUpperCase() + ' ' : ''}users found${searchTerm ? ` matching "${searchTerm}"` : ''}`
                                : 'No users yet'}
                        </div>
                    )}
                </div>

                {/* Legend */}
                <div className="mt-6 bg-white rounded-xl shadow p-4">
                    <h3 className="text-sm font-medium text-slate-700 mb-3">Actions Legend</h3>
                    <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-amber-600" />
                            <span className="text-slate-600">Energy Chart</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CalendarDays className="w-4 h-4 text-indigo-600" />
                            <span className="text-slate-600">Calendar</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Pencil className="w-4 h-4 text-blue-600" />
                            <span className="text-slate-600">Edit User</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <ToggleRight className="w-5 h-5 text-amber-600" />
                            <span className="text-slate-600">Toggle PRO Access</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <UserCheck className="w-4 h-4 text-emerald-600" />
                            <span className="text-slate-600">Promote to Admin</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <UserX className="w-4 h-4 text-purple-600" />
                            <span className="text-slate-600">Remove Admin</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Trash2 className="w-4 h-4 text-red-600" />
                            <span className="text-slate-600">Delete User</span>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Edit User Modal */}
            {editingUser && (
                <EditUserModal
                    user={editingUser}
                    onClose={() => setEditingUser(null)}
                    onSave={fetchData}
                    token={token}
                />
            )}
        </div>
    );
}
